import { Component, OnInit, OnDestroy} from '@angular/core';
import Chart from 'chart.js';import * as _ from 'lodash';
import { ReadingsService } from '../../../services/readings.service';
import ReadingsModel from '../../../services/readings.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import PreferencesModel from 'src/app/services/preferences.model';
import { PreferencesService } from 'src/app/services/preferences.service';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import PersonsModel from '../../../services/persons.model';
import {PersonsService} from '../../../services/persons.service';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component WaterQualityComponent
 * @selector app-water-quality
 * @description
 * The `WaterQualityComponent` is responsible for displaying water quality metrics within the Farm Metrics dashboard.
 * It visualizes key water parameters across ponds, including:
 * 1. **pH**: Acidity/Basicity levels.
 * 2. **Salinity**: Salt concentration.
 * 3. **Dissolved Oxygen (DO)**: Oxygen levels available for aquatic life.
 * 4. **Temperature**: Water temperature.
 * 5. **Alkalinity**: Capacity to neutralize acid.
 * 6. **Turbidity**: Cloudiness or haziness of the fluid.
 *
 * ## Key Responsibilities:
 * - **Data Aggregation**: Fetches water quality readings and aggregates data for summary charts.
 * - **Visualization**: Renders interactive charts (Doughnut or Bar) using Chart.js for each parameter.
 * - **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Provider, etc.).
 * - **User Preferences**: Persists user's preferred chart type (Bar vs Doughnut) for each metric category.
 * - **Detailed Reporting**: Displays tabular lists of water quality details alongside the charts.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: View water quality metrics for their specific farm business.
 * - **Farm Manager**: View metrics for farms assigned to them.
 * - **Business Admin (Provider)**: View metrics for farms associated with the provider.
 * - **Provider Manager**: View metrics for farms assigned to their technicians.
 * - **Employee**: View metrics for all farm sites (internal admin).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessName`: Business name.
 * - `loginBusinessCategory`: Business category.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `phType`, `salinityType`, `doType`, `tempType`, `alkalinityType`, `turbidityType`: Saved preferences for chart types.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Identifies user context from `localStorage`.
 *    - Fetches relevant farm sites based on role.
 *    - Calls `getOnLoadCallsData` with farm site IDs.
 * 2. **Data Fetching (`getOnLoadCallsData`)**:
 *    - Fetches summaries for pH, Salinity, DO, Temperature, Alkalinity, Turbidity, and detailed readings concurrently.
 *    - Prepares chart data (`piekeys`, `piedata`, etc.).
 *    - Renders charts (`preparepHChart`, `prepareSalinityChart`, etc.).
 * 3. **Interaction**:
 *    - User toggles chart types via selection methods (e.g., `phgraphselection`).
 *    - User searches lists via search methods (e.g., `searchpHBusiness`).
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-water-quality',
  templateUrl: './water-quality.component.html',
  styleUrls: ['./water-quality.component.scss']
})
export class WaterQualityComponent implements OnInit, OnDestroy{
  /** @type {Array<PersonsModel>} List of technicians (for Provider Manager role). */
  public technicians: Array<PersonsModel>;

  /** @type {Array<any>} List of technician IDs. */
  public technicianIds = new Array();

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PreferencesModel>} User preferences for chart display settings. */
  public preferences: Array<PreferencesModel>;

  /** @type {Array<ReadingsModel>} List of water quality readings (summary). */
  public readings: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings grouped by pH location. */
  public pHLocations: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings grouped by Salinity location. */
  public salinityLocations: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings grouped by DO location. */
  public doLocations: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings grouped by Temperature location. */
  public tempratureLocations: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings grouped by Alkalinity location. */
  public alkalinityLocations: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} List of readings grouped by Turbidity location. */
  public turbidityLocations: Array<ReadingsModel>;

  /** @type {Array<ReadingsModel>} Detailed list of water quality readings. */
  public waterQualityList: Array<ReadingsModel>;

  /** @type {object} Keys (labels) for the pH chart. */
  public piekeys: object;

  /** @type {object} Data values for the pH chart. */
  public piedata: object;

  /** @type {string} Title for the pH chart. */
  public charttitle: string;

  /** @type {object} Keys (labels) for the Salinity chart. */
  public salinitypiekeys: object;

  /** @type {object} Data values for the Salinity chart. */
  public salinitypiedata: object;

  /** @type {string} Title for the Salinity chart. */
  public salinitycharttitle: string;

  /** @type {object} Keys (labels) for the DO chart. */
  public dopiekeys: object;

  /** @type {object} Data values for the DO chart. */
  public dopiedata: object;

  /** @type {string} Title for the DO chart. */
  public docharttitle: string;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if the page data has loaded.
   */
  public pgLoad = false;

  /** @type {object} Keys (labels) for the Temperature chart. */
  public temppiekeys: object;

  /** @type {object} Data values for the Temperature chart. */
  public temppiedata: object;

  /** @type {string} Title for the Temperature chart. */
  public tempcharttitle: string;

  /** @type {object} Keys (labels) for the Alkalinity chart. */
  public alkalinitypiekeys: object;

  /** @type {object} Data values for the Alkalinity chart. */
  public alkalinitypiedata: object;

  /** @type {string} Title for the Alkalinity chart. */
  public alkalinitycharttitle: string;

  /** @type {object} Keys (labels) for the Turbidity chart. */
  public turbiditypiekeys: object;

  /** @type {object} Data values for the Turbidity chart. */
  public turbiditypiedata: object;

  /** @type {string} Title for the Turbidity chart. */
  public turbiditycharttitle: string;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {Array<any>} List of farm site IDs used for data fetching. */
  public farmSiteIds = new Array();

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /*doc charts declaration*/
  /** @type {string} Current chart type for pH (e.g., 'bar', 'doughnut'). */
  public phType: string;

  /** @type {any} Configuration object for the pH chart. */
  public phgraphconfig: any;

  /** @type {any} Chart.js instance for the pH chart. */
  public phgraph: any;

  /** @type {string} Current chart type for Salinity (e.g., 'bar', 'doughnut'). */
  public salinityType: string;

  /** @type {any} Configuration object for the Salinity chart. */
  public salinitygraphconfig: any;

  /** @type {any} Chart.js instance for the Salinity chart. */
  public salinitygraph: any;

  /** @type {string} Current chart type for DO (e.g., 'bar', 'doughnut'). */
  public doType: string;

  /** @type {any} Configuration object for the DO chart. */
  public dographconfig: any;

  /** @type {any} Chart.js instance for the DO chart. */
  public dograph: any;

  /** @type {string} Current chart type for Temperature (e.g., 'bar', 'doughnut'). */
  public tempType: string;

  /** @type {any} Configuration object for the Temperature chart. */
  public tempgraphconfig: any;

  /** @type {any} Chart.js instance for the Temperature chart. */
  public tempgraph: any;

  /** @type {string} Current chart type for Alkalinity (e.g., 'bar', 'doughnut'). */
  public alkalinityType: string;

  /** @type {any} Configuration object for the Alkalinity chart. */
  public alkalinitygraphconfig: any;

  /** @type {any} Chart.js instance for the Alkalinity chart. */
  public alkalinitygraph: any;

  /** @type {string} Current chart type for Turbidity (e.g., 'bar', 'doughnut'). */
  public turbidityType: string;

  /** @type {any} Configuration object for the Turbidity chart. */
  public turbiditygraphconfig: any;

  /** @type {any} Chart.js instance for the Turbidity chart. */
  public turbiditygraph: any;

  /** @type {string} Default chart type if preference not found. */
  public chartType: string;

  /** @type {boolean} Flag indicating if the initial response has loaded. */
  public responseLoad: boolean;

  /** @type {string} Search keyword for pH list. */
  public pHsearch: string;

  /** @type {string} Search keyword for DO list. */
  public dosearch: string;

  /** @type {string} Search keyword for Temperature list. */
  public tempsearch: string;

  /** @type {string} Search keyword for Alkalinity list. */
  public alkalinitysearch: string;

  /** @type {string} Search keyword for Salinity list. */
  public salinitysearch: string;

  /** @type {string} Search keyword for Turbidity list. */
  public turbiditysearch: string;

  /**
   * @constructor
   * @description Initializes the component and services.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {PersonsService} personService - Service for person-related operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PreferencesService} preferencesService - Service for managing user preferences.
   * @param {ReadingsService} readingsService - Service for fetching water quality readings.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(public sidenavaction: Sidenavaction, public personService: PersonsService, public farmSiteService: FarmSiteService, public preferencesService: PreferencesService, public readingsService: ReadingsService, private toastr: ToastrService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) { }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description
   * Lifecycle hook that initializes the component.
   * 1. Sets the previous route.
   * 2. Retrieves user context from localStorage.
   * 3. Determines the chart type preferences for all water parameters.
   * 4. Fetches farm sites based on the user's role and business category.
   * 5. Calls `getOnLoadCallsData` to fetch metrics data.
   *
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.farmSiteIds = new Array();
      if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') {
        this.phType = localStorage.getItem('phType');
        this.salinityType = localStorage.getItem('salinityType');
        this.doType = localStorage.getItem('doType');
        this.tempType = localStorage.getItem('tempType');
        this.alkalinityType = localStorage.getItem('alkalinityType');
        this.turbidityType = localStorage.getItem('turbidityType');
        if (this.phType == '') {
          this.chartType = 'bar';
        }
        if (this.salinityType == '') {
          this.chartType = 'bar';
        }
        if (this.doType == '') {
          this.chartType = 'bar';
        }
        if (this.tempType == '') {
          this.chartType = 'bar';
        }
        if (this.alkalinityType == '') {
          this.chartType = 'bar';
        }
        if (this.turbidityType == '') {
          this.chartType = 'bar';
        }
      }
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      }
      this.responseLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * Cancels ongoing HTTP requests for FarmSite, Readings, and Preferences services.
   *
   * @returns {void}
   */
public ngOnDestroy(): void {
  this.readingsService.cancelOnGoingHttpRequests();
  this.farmSiteService.cancelOnGoingHttpRequests();
  this.preferencesService.cancelOnGoingHttpRequests();
}

  /**
   * @method getOnLoadCallsData
   * @async
   * @description
   * Fetches water quality metrics data for the provided farm sites.
   * 1. Calls summary APIs for pH, Salinity, DO, Temperature, Alkalinity, Turbidity, and detailed readings concurrently.
   * 2. Processes summary data for all charts.
   * 3. Loads user preferences for chart type if applicable.
   * 4. Renders the charts.
   *
   * @param {Array<any>} farmSiteIds - List of farm site IDs to fetch data for.
   * @returns {Promise<void>}
   */
  public async getOnLoadCallsData(farmSiteIds:any) {
    try {
      let promiseResults = new Array();
      await Promise.all([
        await this.readingsService.getPHSummary(farmSiteIds),
        await this.readingsService.getSalinitySummary(farmSiteIds),
        await this.readingsService.getDOTwoSummary(farmSiteIds),
        await this.readingsService.getTemperatureSummary(farmSiteIds),
        await this.readingsService.getAlkalinitySummary(farmSiteIds),
        await this.readingsService.getTurbiditySummary(farmSiteIds),
        await this.readingsService.getWaterQualitydetails(farmSiteIds)
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.readings = promiseResults[0];
      let chartdataobj = Object.assign({}, this.readings);
      delete chartdataobj['pondsCount' as any];
      this.piekeys = Object.keys(chartdataobj);
      this.piedata = Object.values(chartdataobj);
      this.pgLoad = true;
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getChattypeinDB();
      } else {
        let phcharttype = this.phType || 'doughnut';
        this.preparepHChart(phcharttype);
      }
      this.readings = promiseResults[1];
      let salinitychartdataobj = Object.assign({}, this.readings);
      delete salinitychartdataobj['pondsCount' as any];
      this.salinitypiekeys = Object.keys(salinitychartdataobj);
      this.salinitypiedata = Object.values(salinitychartdataobj);
      this.pgLoad = true;
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getChattypeinDB();
      } else {
        let salinitycharttype = this.salinityType || 'doughnut';
        this.prepareSalinityChart(salinitycharttype);
      }
      this.readings = promiseResults[2];
      let do2chartdataobj = Object.assign({}, this.readings);
      delete do2chartdataobj['pondsCount' as any];
      this.dopiekeys = Object.keys(do2chartdataobj);
      this.dopiedata = Object.values(do2chartdataobj);
      this.pgLoad = true;
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getChattypeinDB();
      } else {
        let docharttype = this.doType || 'doughnut';
        this.prepareDoChart(docharttype);
      }
      this.readings = promiseResults[3];
      let temperaturechartdataobj = Object.assign({}, this.readings);
      delete temperaturechartdataobj['pondsCount' as any];
      this.temppiekeys = Object.keys(temperaturechartdataobj);
      this.temppiedata = Object.values(temperaturechartdataobj);
      this.pgLoad = true;
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getChattypeinDB();
      } else {
        let tempcharttype = this.tempType || 'doughnut';
        this.prepareTempChart(tempcharttype);
      }
      this.readings = promiseResults[4];
      let alkalinitychartdataobj = Object.assign({}, this.readings);
      delete alkalinitychartdataobj['pondsCount' as any];
      this.alkalinitypiekeys = Object.keys(alkalinitychartdataobj);
      this.alkalinitypiedata = Object.values(alkalinitychartdataobj);
      this.pgLoad = true;
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getChattypeinDB();
      } else {
        let alkalinitycharttype = this.alkalinityType || 'doughnut';
        this.prepareAlkalinityChart(alkalinitycharttype);
      }
      this.readings = promiseResults[5];
      let turbiditychartdataobj = Object.assign({}, this.readings);
      delete turbiditychartdataobj['pondsCount' as any];
      this.turbiditypiekeys = Object.keys(turbiditychartdataobj);
      this.turbiditypiedata = Object.values(turbiditychartdataobj);
      this.pgLoad = true;
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getChattypeinDB();
      } else {
        let turbiditycharttype = this.turbidityType || 'doughnut';
        this.prepareTurbidityChart(turbiditycharttype);
      }
      this.waterQualityList = promiseResults[6];
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getChattypeinDB
   * @async
   * @description
   * Fetches user preferences for chart types from the database.
   * Updates the chart type variables and re-renders charts if preferences exist.
   * @returns {Promise<void>}
   */
  public async getChattypeinDB() {
    try {
      this.preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
      if (this.preferences.length > 0) {
        this.chartType = this.preferences[0].chartType;
      } else {
        this.chartType = 'doughnut';
      }
      this.preparepHChart(this.chartType);
      this.prepareSalinityChart(this.chartType);
      this.prepareDoChart(this.chartType);
      this.prepareTempChart(this.chartType);
      this.prepareAlkalinityChart(this.chartType);
      this.prepareTurbidityChart(this.chartType);
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /* End get all ponds by Turbidity*/

  /**
   * @method preparepHChart
   * @description
   * Configures and renders the pH chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `phtype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} phtype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.preparepHChart('doughnut');
   * @returns {void}
   */
  public preparepHChart(phtype: string) {
    this.charttitle = 'Ponds by pH' + ' ' + ' ' + ' ';
    if (this.readings['pondsCount' as any]) {
      this.charttitle += this.readings['pondsCount' as any] + ' ponds';
    }
    let ctx: any = document.getElementById('myChart');
    let ctx1: any = ctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.piedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let pHnoData = false;
    if (isallareZeros.length == 0) {
      pHnoData = true;
    }
    let legenddisply = true;
    if (phtype == 'bar') {
      legenddisply = false;
    }
    this.phgraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.piekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#FAFF75',
            '#6AD276',
            '#FAF307',
            '#DF7401'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.piedata
        }]
      },
      options: {
        legend: {
          // display: pHnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: pHnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.phgraphconfig);
    temp.type = phtype || 'doughnut';
    // FOR NO DATA FOUND
    this.phgraph = new Chart(ctx1, temp);
  }

  // end pg graph

  /**
   * @method phgraphselection
   * @description
   * Handles the user's selection of chart type for pH (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} phtype - The selected chart type.
   * @example
   * this.phgraphselection('bar');
   * @returns {void}
   */
  public phgraphselection(phtype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (phtype == 'bar') {
      let ele = '<canvas id=\'myChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('myChart');
      let ctx1: any = ctx.getContext('2d');

      if (this.phgraph) {
        this.phgraph.destroy();
      }
      this.phgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.phgraphconfig);
      temp.type = 'bar';
      this.phgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('phType', temp.type);
      }
    } else if (phtype == 'doughnut') {
      let ele = '<canvas id=\'myChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('myChart');
      let ctx1: any = ctx.getContext('2d');

      if (this.phgraph) {
        this.phgraph.destroy();
      }
      this.phgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.phgraphconfig);
      temp.type = 'doughnut';
      this.phgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('phType', temp.type);
      }
    }
  }

  /**
   * @method prepareSalinityChart
   * @description
   * Configures and renders the Salinity chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `salinitytype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} salinitytype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareSalinityChart('doughnut');
   * @returns {void}
   */
  public prepareSalinityChart(salinitytype: string) {
    this.salinitycharttitle = 'Ponds by salinity' + ' ' + ' ' + ' ';
    if (this.readings['pondsCount' as any]) {
      this.salinitycharttitle += this.readings['pondsCount' as any] + ' ponds';
    }
    let salinityctx: any = document.getElementById('salinityChart');
    let salinityctx1: any = salinityctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.salinitypiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let salinitynoData = false;
    if (isallareZeros.length == 0) {
      salinitynoData = true;
    }

    let legenddisply = true;
    if (salinitytype == 'bar') {
      legenddisply = false;
    }
    this.salinitygraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.salinitypiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#6AD276',
            '#FAFF75',
            '#FAF307',
            '#DF7401'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.salinitypiedata
        }]
      },
      options: {
        legend: {
          // display: salinitynoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: salinitynoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.salinitygraphconfig);
    temp.type = salinitytype || 'doughnut';
    // FOR NO DATA FOUND
    this.salinitygraph = new Chart(salinityctx1, temp);
  }

  // end salinity graph

  /**
   * @method salinitygraphselection
   * @description
   * Handles the user's selection of chart type for Salinity (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} salinitytype - The selected chart type.
   * @example
   * this.salinitygraphselection('bar');
   * @returns {void}
   */
  public salinitygraphselection(salinitytype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (salinitytype == 'bar') {
      let ele = '<canvas id=\'salinityChart\'></canvas>';
      $('#salinitycanvasarea').html(ele);
      let salinityctx: any = document.getElementById('salinityChart');
      let salinityctx1: any = salinityctx.getContext('2d');

      if (this.salinitygraph) {
        this.salinitygraph.destroy();
      }
      this.salinitygraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.salinitygraphconfig);
      temp.type = 'bar';
      this.salinitygraph = new Chart(salinityctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('salinityType', temp.type);
      }
    } else if (salinitytype == 'doughnut') {
      let ele = '<canvas id=\'salinityChart\'></canvas>';
      $('#salinitycanvasarea').html(ele);
      let salinityctx: any = document.getElementById('salinityChart');
      let salinityctx1: any = salinityctx.getContext('2d');

      if (this.salinitygraph) {
        this.salinitygraph.destroy();
      }
      this.salinitygraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.salinitygraphconfig);
      temp.type = 'doughnut';
      this.salinitygraph = new Chart(salinityctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('salinityType', temp.type);
      }
    }
  }


  /**
   * @method prepareDoChart
   * @description
   * Configures and renders the DO chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `dotype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} dotype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareDoChart('doughnut');
   * @returns {void}
   */
  public prepareDoChart(dotype: string) {
    this.docharttitle = 'Ponds by DO' + ' ' + ' ' + ' ';
    if (this.readings['pondsCount' as any]) {
      this.docharttitle += this.readings['pondsCount' as any] + ' ponds';
    }
    let doctx: any = document.getElementById('doChart');
    let doctx1: any = doctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.dopiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let donoData = false;
    if (isallareZeros.length == 0) {
      donoData = true;
    }
    // for NO DATA FOUND
    let legenddisply = true;
    if (dotype == 'bar') {
      legenddisply = false;
    }
    this.dographconfig = {

      // type: 'doughnut',

      data: {
        labels: this.dopiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#FAFF75',
            '#6AD276',
            '#FAF307',
            '#DF7401'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.dopiedata
        }]
      },
      options: {
        legend: {
          // display: donoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: donoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.dographconfig);
    temp.type = dotype || 'doughnut';
    // FOR NO DATA FOUND
    this.dograph = new Chart(doctx1, temp);
  }

  // end do graph

  /**
   * @method dographselection
   * @description
   * Handles the user's selection of chart type for DO (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} dotype - The selected chart type.
   * @example
   * this.dographselection('bar');
   * @returns {void}
   */
  public dographselection(dotype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (dotype == 'bar') {
      let ele = '<canvas id=\'doChart\'></canvas>';
      $('#docanvasarea').html(ele);
      let doctx: any = document.getElementById('doChart');
      let doctx1: any = doctx.getContext('2d');

      if (this.dograph) {
        this.dograph.destroy();
      }
      this.dographconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.dographconfig);
      temp.type = 'bar';
      this.dograph = new Chart(doctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('doType', temp.type);
      }
    } else if (dotype == 'doughnut') {
      let ele = '<canvas id=\'doChart\'></canvas>';
      $('#docanvasarea').html(ele);
      let doctx: any = document.getElementById('doChart');
      let doctx1 = doctx.getContext('2d');

      if (this.dograph) {
        this.dograph.destroy();
      }
      this.dographconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.dographconfig);
      temp.type = 'doughnut';
      this.dograph = new Chart(doctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('doType', temp.type);
      }
    }
  }

  /**
   * @method prepareTempChart
   * @description
   * Configures and renders the Temperature chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `temptype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} temptype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareTempChart('doughnut');
   * @returns {void}
   */
  public prepareTempChart(temptype: string) {
    this.tempcharttitle = 'Ponds by Temperature' + ' ' + ' ' + ' ';
    if (this.readings['pondsCount' as any]) {
      this.tempcharttitle += this.readings['pondsCount' as any] + ' ponds';
    }
    let tempctx: any = document.getElementById('tempChart');
    let tempctx1: any = tempctx.getContext('2d');

    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.temppiedata, function (o) {
      return o != 0;
    }); //if all are not zeros
    let tempnoData = false;
    if (isallareZeros.length == 0) {
      tempnoData = true;
    }
    // for NO DATA FOUND

    let legenddisply = true;
    if (temptype == 'bar') {
      legenddisply = false;
    }
    this.tempgraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.temppiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#FAFF75',
            '#6AD276',
            '#FAF307',
            '#DF7401'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.temppiedata
        }]
      },
      options: {
        legend: {
          // display: tempnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: tempnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.tempgraphconfig);
    temp.type = temptype || 'doughnut';
    // FOR NO DATA FOUND
    this.tempgraph = new Chart(tempctx1, temp);
  }

  /**
   * @method tempgraphselection
   * @description
   * Handles the user's selection of chart type for Temperature (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} temptype - The selected chart type.
   * @example
   * this.tempgraphselection('bar');
   * @returns {void}
   */
  public tempgraphselection(temptype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (temptype == 'bar') {
      let ele = '<canvas id=\'tempChart\'></canvas>';
      $('#tempcanvasarea').html(ele);
      let tempctx: any = document.getElementById('tempChart');
      let tempctx1: any = tempctx.getContext('2d');

      if (this.tempgraph) {
        this.tempgraph.destroy();
      }
      this.tempgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.tempgraphconfig);
      temp.type = 'bar';
      this.tempgraph = new Chart(tempctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('tempType', temp.type);
      }
    } else if (temptype == 'doughnut') {
      let ele = '<canvas id=\'tempChart\'></canvas>';
      $('#tempcanvasarea').html(ele);
      let tempctx: any = document.getElementById('tempChart');
      let tempctx1: any = tempctx.getContext('2d');

      if (this.tempgraph) {
        this.tempgraph.destroy();
      }
      this.tempgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.tempgraphconfig);
      temp.type = 'doughnut';
      this.tempgraph = new Chart(tempctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('tempType', temp.type);
      }
    }
  }


  // end temp graph

  /**
   * @method prepareAlkalinityChart
   * @description
   * Configures and renders the Alkalinity chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `alkalinitytype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} alkalinitytype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareAlkalinityChart('doughnut');
   * @returns {void}
   */
  public prepareAlkalinityChart(alkalinitytype: string) {
    this.alkalinitycharttitle = 'Ponds by Alkalinity' + ' ' + ' ' + ' ';
    if (this.readings['pondsCount' as any]) {
      this.alkalinitycharttitle += this.readings['pondsCount' as any] + ' ponds';
    }
    let alkalinityctx: any = document.getElementById('alkalinityChart');
    let alkalinityctx1: any = alkalinityctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.alkalinitypiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let alkalinitynoData = false;
    if (isallareZeros.length == 0) {
      alkalinitynoData = true;
    }
    let legenddisply = true;
    if (alkalinitytype == 'bar') {
      legenddisply = false;
    }
    this.alkalinitygraphconfig = {
      //  type: 'doughnut',

      data: {
        labels: this.alkalinitypiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#6AD276',
            '#FAFF75',
            '#FAF307',
            '#DF7401'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.alkalinitypiedata
        }]
      },
      options: {
        legend: {
          // display: alkalinitynoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: alkalinitynoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.alkalinitygraphconfig);
    temp.type = alkalinitytype || 'doughnut';
    // FOR NO DATA FOUND
    this.alkalinitygraph = new Chart(alkalinityctx1, temp);
  }

  // end alkalinity graph

  /**
   * @method alkalinitygraphselection
   * @description
   * Handles the user's selection of chart type for Alkalinity (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} alkalinitytype - The selected chart type.
   * @example
   * this.alkalinitygraphselection('bar');
   * @returns {void}
   */
  public alkalinitygraphselection(alkalinitytype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (alkalinitytype == 'bar') {
      let ele = '<canvas id=\'alkalinityChart\'></canvas>';
      $('#alkalinitycanvasarea').html(ele);
      let alkalinityctx: any = document.getElementById('alkalinityChart');
      let alkalinityctx1: any = alkalinityctx.getContext('2d');

      if (this.alkalinitygraph) {
        this.alkalinitygraph.destroy();
      }
      this.alkalinitygraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.alkalinitygraphconfig);
      temp.type = 'bar';
      this.alkalinitygraph = new Chart(alkalinityctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('alkalinityType', temp.type);
      }
    } else if (alkalinitytype == 'doughnut') {
      let ele = '<canvas id=\'alkalinityChart\'></canvas>';
      $('#alkalinitycanvasarea').html(ele);
      let alkalinityctx: any = document.getElementById('alkalinityChart');
      let alkalinityctx1: any = alkalinityctx.getContext('2d');

      if (this.alkalinitygraph) {
        this.alkalinitygraph.destroy();
      }
      this.alkalinitygraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.alkalinitygraphconfig);
      temp.type = 'doughnut';
      this.alkalinitygraph = new Chart(alkalinityctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('alkalinityType', temp.type);
      }
    }
  }


  /**
   * @method prepareTurbidityChart
   * @description
   * Configures and renders the Turbidity chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `turbiditytype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} turbiditytype - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareTurbidityChart('doughnut');
   * @returns {void}
   */
  public prepareTurbidityChart(turbiditytype: string) {
    this.turbiditycharttitle = 'Ponds by Turbidity' + ' ' + ' ' + ' ';
    if (this.readings['pondsCount' as any]) {
      this.turbiditycharttitle += this.readings['pondsCount' as any] + ' ponds';
    }
    let turbidityctx: any = document.getElementById('turbidityChart');
    let turbidityctx1: any = turbidityctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.turbiditypiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let turbiditynoData = false;
    if (isallareZeros.length == 0) {
      turbiditynoData = true;
    }
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (turbiditytype == 'bar') {
      legenddisply = false;
    }
    this.turbiditygraphconfig = {
      // type: 'doughnut',

      data: {
        labels: this.turbiditypiekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#FE9A2E',
            '#FAFF75',
            '#FAF307',
            '#6AD276',
            '#DF7401'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.turbiditypiedata
        }]
      },
      options: {
        legend: {
          // display: turbiditynoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: turbiditynoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.turbiditygraphconfig);
    temp.type = turbiditytype || 'doughnut';
    // FOR NO DATA FOUND
    this.turbiditygraph = new Chart(turbidityctx1, temp);
  }

  // end turbidity graph

  /**
   * @method turbiditygraphselection
   * @description
   * Handles the user's selection of chart type for Turbidity (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} turbiditytype - The selected chart type.
   * @example
   * this.turbiditygraphselection('bar');
   * @returns {void}
   */
  public turbiditygraphselection(turbiditytype: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (turbiditytype == 'bar') {
      let ele = '<canvas id=\'turbidityChart\'></canvas>';
      $('#turbiditycanvasarea').html(ele);
      let turbidityctx: any = document.getElementById('turbidityChart');
      let turbidityctx1: any = turbidityctx.getContext('2d');

      if (this.turbiditygraph) {
        this.turbiditygraph.destroy();
      }
      this.turbiditygraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.turbiditygraphconfig);
      temp.type = 'bar';
      this.turbiditygraph = new Chart(turbidityctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('turbidityType', temp.type);
      }
    } else if (turbiditytype == 'doughnut') {
      let ele = '<canvas id=\'turbidityChart\'></canvas>';
      $('#turbiditycanvasarea').html(ele);
      let turbidityctx: any = document.getElementById('turbidityChart');
      let turbidityctx1: any = turbidityctx.getContext('2d');

      if (this.turbiditygraph) {
        this.turbiditygraph.destroy();
      }
      this.turbiditygraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.turbiditygraphconfig);
      temp.type = 'doughnut';
      this.turbiditygraph = new Chart(turbidityctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('turbidityType', temp.type);
      }
    }
  }

  /**
   * @method getAllpHLocation
   * @async
   * @description
   * Fetches pH location summary data.
   * Populates `pHLocations`.
   * @returns {Promise<void>}
   */
  public async getAllpHLocation() {
    try {
      this.pHLocations = await this.readingsService.getPHLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getAllSalinityLocation
   * @async
   * @description
   * Fetches Salinity location summary data.
   * Populates `salinityLocations`.
   * @returns {Promise<void>}
   */
  public async getAllSalinityLocation() {
    try {
      this.salinityLocations = await this.readingsService.getSalinityLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getAllDoLocation
   * @async
   * @description
   * Fetches DO location summary data.
   * Populates `doLocations`.
   * @returns {Promise<void>}
   */
  public async getAllDoLocation() {
    try {
      this.doLocations = await this.readingsService.getDOTwoLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getAllTempLocation
   * @async
   * @description
   * Fetches Temperature location summary data.
   * Populates `tempratureLocations`.
   * @returns {Promise<void>}
   */
  public async getAllTempLocation() {
    try {
      this.tempratureLocations = await this.readingsService.getTemperatureLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getAllAlkalinityLocation
   * @async
   * @description
   * Fetches Alkalinity location summary data.
   * Populates `alkalinityLocations`.
   * @returns {Promise<void>}
   */
  public async getAllAlkalinityLocation() {
    try {
      this.alkalinityLocations = await this.readingsService.getAlkalinityLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getAllTurbidityLocation
   * @async
   * @description
   * Fetches Turbidity location summary data.
   * Populates `turbidityLocations`.
   * @returns {Promise<void>}
   */
  public async getAllTurbidityLocation() {
    try {
      this.turbidityLocations = await this.readingsService.getTurbidityLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method charttypesavetoDB
   * @async
   * @description
   * Saves the user's chart type preference to the database via `PreferencesService`.
   *
   * @param {string} type - The chart type to save.
   * @example
   * this.charttypesavetoDB('bar');
   * @returns {Promise<void>}
   */
  public async charttypesavetoDB(type: string) {
    try {
      this.preferences = await this.preferencesService.addPreferences(localStorage.businessID, localStorage.userID, type);
      this.pgLoad = true;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method searchpHBusiness
   * @description
   * Filters the displayed pH list based on the `pHsearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunph`.
   * @example
   * this.searchpHBusiness();
   * @returns {void}
   */
  public searchpHBusiness() {
    let value = this.pHsearch.toLowerCase();
    $('.searchfunph').filter( () => {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchSalinityBusiness
   * @description
   * Filters the displayed Salinity list based on the `salinitysearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunsalinity`.
   * @example
   * this.searchSalinityBusiness();
   * @returns {void}
   */
  public searchSalinityBusiness() {
    let value = this.salinitysearch.toLowerCase();
    $('.searchfunsalinity').filter( () => {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchDOBusiness
   * @description
   * Filters the displayed DO list based on the `dosearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfundo`.
   * @example
   * this.searchDOBusiness();
   * @returns {void}
   */
  public searchDOBusiness() {
    let value = this.dosearch.toLowerCase();
    $('.searchfundo').filter( () => {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchTempBusiness
   * @description
   * Filters the displayed Temperature list based on the `tempsearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfuntemp`.
   * @example
   * this.searchTempBusiness();
   * @returns {void}
   */
  public searchTempBusiness() {
    let value = this.tempsearch.toLowerCase();
    $('.searchfuntemp').filter( () => {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchAlkalinityBusiness
   * @description
   * Filters the displayed Alkalinity list based on the `alkalinitysearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunalkalinity`.
   * @example
   * this.searchAlkalinityBusiness();
   * @returns {void}
   */
  public searchAlkalinityBusiness() {
    let value = this.alkalinitysearch.toLowerCase();
    $('.searchfunalkalinity').filter( () => {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchTurbidityBusiness
   * @description
   * Filters the displayed Turbidity list based on the `turbiditysearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunturbidity`.
   * @example
   * this.searchTurbidityBusiness();
   * @returns {void}
   */
  public searchTurbidityBusiness() {
    let value = this.turbiditysearch.toLowerCase();
    $('.searchfunturbidity').filter( () => {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }
}
